import React from 'react';
import { XIcon } from 'lucide-react';
import { format } from 'date-fns';
interface ViewTicketDetailsModalProps {
  onClose: () => void;
  onEdit?: () => void;
  ticket: any;
  isLocked?: boolean;
}
const ViewTicketDetailsModal: React.FC<ViewTicketDetailsModalProps> = ({
  onClose,
  onEdit,
  ticket,
  isLocked = false
}) => {
  // Format date in DD MMM YYYY format
  const formatDate = dateString => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (e) {
      return dateString;
    }
  };
  return <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" data-id="element-432">
      <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4" data-id="element-433">
        <div className="flex items-start justify-between p-4 border-b border-gray-200" data-id="element-434">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-435">
            Ticket Details
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" aria-label="Close" data-id="element-436">
            <XIcon className="h-6 w-6" data-id="element-437" />
          </button>
        </div>
        <div className="p-6" data-id="element-438">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-439">
            <div data-id="element-440">
              <h4 className="text-sm font-medium text-gray-500 mb-4" data-id="element-441">
                Ticket Information
              </h4>
              <div className="space-y-3" data-id="element-442">
                <div data-id="element-443">
                  <label className="block text-xs text-gray-500" data-id="element-444">
                    Ticket Number
                  </label>
                  <p className="font-medium" data-id="element-445">{ticket.ticketNumber}</p>
                </div>
                <div data-id="element-446">
                  <label className="block text-xs text-gray-500" data-id="element-447">
                    Passenger Name
                  </label>
                  <p className="font-medium" data-id="element-448">{ticket.paxName}</p>
                </div>
                <div data-id="element-449">
                  <label className="block text-xs text-gray-500" data-id="element-450">Sector</label>
                  <p className="font-medium" data-id="element-451">{ticket.sector}</p>
                </div>
                <div data-id="element-452">
                  <label className="block text-xs text-gray-500" data-id="element-453">
                    Sector Code
                  </label>
                  <p className="font-medium" data-id="element-454">{ticket.sectorCode}</p>
                </div>
                <div data-id="element-455">
                  <label className="block text-xs text-gray-500" data-id="element-456">
                    Airline Code
                  </label>
                  <p className="font-medium" data-id="element-457">{ticket.airlineCode}</p>
                </div>
                <div data-id="element-458">
                  <label className="block text-xs text-gray-500" data-id="element-459">Class</label>
                  <p className="font-medium" data-id="element-460">{ticket.class}</p>
                </div>
              </div>
            </div>
            <div data-id="element-461">
              <h4 className="text-sm font-medium text-gray-500 mb-4" data-id="element-462">
                Financial Details
              </h4>
              <div className="space-y-3" data-id="element-463">
                <div data-id="element-464">
                  <label className="block text-xs text-gray-500" data-id="element-465">
                    Issue Date
                  </label>
                  <p className="font-medium" data-id="element-466">{formatDate(ticket.issueDate)}</p>
                </div>
                <div data-id="element-467">
                  <label className="block text-xs text-gray-500" data-id="element-468">
                    Air Fare
                  </label>
                  <p className="font-medium" data-id="element-469">
                    ${ticket.airFare?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div data-id="element-470">
                  <label className="block text-xs text-gray-500" data-id="element-471">Tax</label>
                  <p className="font-medium" data-id="element-472">
                    ${ticket.tax?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div data-id="element-473">
                  <label className="block text-xs text-gray-500" data-id="element-474">
                    Total Cost
                  </label>
                  <p className="font-medium" data-id="element-475">
                    ${ticket.totalCost?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div data-id="element-476">
                  <label className="block text-xs text-gray-500" data-id="element-477">
                    Reconciliation Status
                  </label>
                  <p className="font-medium" data-id="element-478">
                    {ticket.reconciled ? 'Reconciled' : 'Pending'}
                  </p>
                </div>
                {ticket.type === 'ATC' && <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-id="element-479">
                    <p className="text-amber-700 font-medium" data-id="element-480">ATC Entry</p>
                  </div>}
              </div>
            </div>
          </div>
          {ticket.type === 'ATC' && <div className="mt-6 border-t pt-4" data-id="element-481">
              <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-482">
                ATC Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-483">
                <div data-id="element-484">
                  <label className="block text-xs text-gray-500" data-id="element-485">
                    Reference Number
                  </label>
                  <p className="font-medium" data-id="element-486">
                    {ticket.referenceNumber || 'N/A'}
                  </p>
                </div>
                <div data-id="element-487">
                  <label className="block text-xs text-gray-500" data-id="element-488">
                    Tour Code
                  </label>
                  <p className="font-medium" data-id="element-489">{ticket.tourCode || 'N/A'}</p>
                </div>
                <div data-id="element-490">
                  <label className="block text-xs text-gray-500" data-id="element-491">
                    Staff Responsible
                  </label>
                  <p className="font-medium" data-id="element-492">
                    {ticket.staffResponsible || 'N/A'}
                  </p>
                </div>
                {ticket.cancelDate && <>
                    <div data-id="element-493">
                      <label className="block text-xs text-gray-500" data-id="element-494">
                        Cancel Date
                      </label>
                      <p className="font-medium" data-id="element-495">
                        {formatDate(ticket.cancelDate)}
                      </p>
                    </div>
                    <div data-id="element-496">
                      <label className="block text-xs text-gray-500" data-id="element-497">
                        Cancel Staff
                      </label>
                      <p className="font-medium" data-id="element-498">
                        {ticket.cancelStaff || 'N/A'}
                      </p>
                    </div>
                  </>}
              </div>
              {ticket.remarks && <div className="mt-4" data-id="element-499">
                  <label className="block text-xs text-gray-500" data-id="element-500">Remarks</label>
                  <p className="font-medium" data-id="element-501">{ticket.remarks}</p>
                </div>}
            </div>}
          {!ticket.type && ticket.remarks && <div className="mt-6 border-t pt-4" data-id="element-502">
              <label className="block text-xs text-gray-500" data-id="element-503">Remarks</label>
              <p className="font-medium" data-id="element-504">{ticket.remarks}</p>
            </div>}
        </div>
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200" data-id="element-505">
          {!isLocked && onEdit && <button type="button" onClick={onEdit} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-506">
              Edit Ticket
            </button>}
          <button type="button" onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-507">
            Close
          </button>
        </div>
      </div>
    </div>;
};
export default ViewTicketDetailsModal;